import { authenticate, login, register } from 'api/auth'
import { IError } from 'api/axios'
import { metricsClient } from 'api/metrics'
import { UserPathwayAlert } from 'features/User'
import { useEffectOnce } from 'hooks/useEffectOnce'
import moment from 'moment'
import React, { ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { router } from 'routes/router'
import { routes } from 'routes/routes'
import { useActions, useAppDispatch, useAppSelector } from 'store/hooks'
import { openSidebar } from 'store/slice/navigation.slice'
import {
  ELicenseType,
  LoginResponse,
  RegisterRequest,
  User,
  UserOrganizationalRoles,
  UserType,
  UserTypeLabels,
  UserTypeTab,
} from 'types'
import { clearLocalStorage, getRoute, getSafeRedirectPath, TOKEN, USER_DATA } from 'utils'
import {
  isRoleAdmin,
  isRoleClient,
  isRoleOrganization,
} from '../../features/Auth/models/AuthRole.model'
import { INCORRECT_CREDENTIALS } from '../../features/Auth/models/authMessages'
import { canUseSubscriptionShell, isSubscriptionGated } from '../../features/Subscription/utils'
import NotificationSys from '../NotificationSystem'
import { timeMeasureSystem } from './TimeMeasureSystem'

const unknown = 'Unknown'

const defaultValue = {
  isTrainee: false,
  isIndividualTrainee: false,
  isCareTeam: false,
  isCaregiver: false,
  isAdmin: false,
  isAnalist: false,
  isOrgUser: false,
  profileName: unknown,
  authLoading: false,
  position: '',
  isSharingEnabled: false,
  regLoading: false,
  login: () => {},
  register: () => {},
  clearUserData: () => {},
  refreshUser: () => Promise.resolve(null),
  checkIsLoggedIn: () => false,
}

type ContextType = {
  user?: User
  checkIsLoggedIn: () => boolean
  isTrainee: boolean
  /** Trainee who bought their own licence — the only trainee whose access depends on a subscription. */
  isIndividualTrainee: boolean
  isCareTeam: boolean
  isCaregiver: boolean
  isAdmin: boolean
  isAnalist: boolean
  isOrgUser: boolean
  profileName: string
  position: string
  isSharingEnabled: boolean
  authLoading: boolean
  regLoading: boolean
  login: (
    email: string,
    password: string,
    onSuccess?: (user: LoginResponse) => void,
    onFail?: (reason: IError) => void,
  ) => void
  register: (request: RegisterRequest) => void
  clearUserData: () => void
  refreshUser: () => Promise<User | null>
}

const AuthContext = React.createContext<ContextType>(defaultValue)

export const useAuthContext = () => {
  return useContext(AuthContext)
}

const AuthProvider = ({ children }: { children: ReactNode }) => {
  const dispatch = useAppDispatch()
  const [authLoading, setAuthLoading] = useState(false)
  const [regLoading, setRegLoading] = useState(false)

  const { signIn } = useActions()
  const user = useAppSelector((s) => s.currentUser) as User

  const checkIsLoggedIn = useCallback(() => {
    return !!localStorage.getItem(TOKEN)
  }, [])

  const checkPathwayChanges = useCallback(() => {
    if (!checkIsLoggedIn() || !user.isTrainingPathwayTypeChanged) return

    NotificationSys.addNotification({
      level: 'info',
      title: <UserPathwayAlert />,
      autoDismiss: 0,
    })
  }, [user, checkIsLoggedIn])

  const onSuccessRegister = useCallback(() => {
    setTimeout(() => {
      router.navigate(routes.dashboard)
      dispatch(openSidebar())
    }, 0)
  }, [dispatch])

  const onSuccessLogin = useCallback(
    (response: LoginResponse) => {
      const { user } = response
      const roles = user?.type
      if (!roles) {
        return
      }

      const searchParams = new URLSearchParams(window.location.search)
      const redirect = getSafeRedirectPath(searchParams.get('redirect'))

      if (redirect) {
        router.navigate(redirect)
      } else if (isRoleAdmin(roles)) {
        const route = getRoute(routes.users, { userType: UserTypeTab.TRAINEE })
        router.navigate(route)
      } else if (isRoleClient(roles)) {
        if (canUseSubscriptionShell(user)) {
          router.navigate(routes.journey)
        } else if (isSubscriptionGated(user)) {
          router.navigate(routes.addSubscription)
        } else {
          router.navigate(routes.journey)
        }
      } else if (isRoleOrganization(roles)) {
        const route = getRoute(routes.orgUsers, { userType: UserTypeTab.TRAINEE })
        router.navigate(route)
      }

      dispatch(openSidebar())
    },
    [dispatch],
  )

  const onFailLogin = (reason: IError) => {
    console.error('Error', reason)
    if (reason.response?.status === 401) {
      NotificationSys.showWarning(INCORRECT_CREDENTIALS)
    }
  }

  const onLogin = useCallback(
    (
      email: string,
      password: string,
      onSuccess: (user: LoginResponse) => void = onSuccessLogin,
      onFail: (reason: IError) => void = onFailLogin,
    ) => {
      setAuthLoading(true)

      login(email, password, { suppressErrorHandler: [401, 403] })
        .catch(onFail)
        .then((response) => {
          if (response) {
            signIn({ user: response.data.user, token: response.data.token })
            metricsClient.setToken(response.data.token)
            timeMeasureSystem.startUserSession()
            onSuccess(response.data)
          }
        })
        .finally(() => {
          setAuthLoading(false)
        })
    },
    [onSuccessLogin, signIn],
  )

  const onRegister = useCallback(
    (request: RegisterRequest) => {
      setRegLoading(true)

      register(request, { suppressErrorHandler: [401] })
        .then((userData) => {
          if (userData) {
            onLogin(request.email, request.password, onSuccessRegister, () => {
              NotificationSys.showWarning(
                "Can't automatically sign-in after register. Please sign-in manually",
              )
            })
          }
        })
        .finally(() => {
          setRegLoading(false)
        })
    },
    [onLogin, onSuccessRegister],
  )

  // updating info about user on page load
  const onAuthenticate = useCallback(() => {
    return authenticate().then((response) => {
      if (response) {
        signIn({ user: response.data.user, token: response.data.token })
        metricsClient.setToken(response.data.token)
        timeMeasureSystem.startUserSession()
        return response.data.user
      }
      return null
    })
  }, [signIn])

  const clearUserData = useCallback(() => {
    timeMeasureSystem.stopUserSession()
    clearLocalStorage()
  }, [])

  const isSharingEnabled = useMemo(() => {
    if (!user?.careTeamContentSharingEnabled) {
      return false
    }

    // Check if sharing expiry date is not earlier than today
    if (user.careTeamContentSharingExpiryDate) {
      const today = moment().startOf('day')
      const expiryDate = moment(user.careTeamContentSharingExpiryDate).startOf('day')
      return expiryDate.isSameOrAfter(today)
    }

    return true
  }, [user])

  useEffectOnce(() => {
    if (checkIsLoggedIn()) {
      onAuthenticate()
    }
  })

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === USER_DATA) {
        if (event.oldValue && event.newValue) {
          const oldUserData: User = JSON.parse(event.oldValue)
          const newUserData: User = JSON.parse(event.newValue)
          // reload page if it is a new user
          if (oldUserData.id === newUserData.id) {
            return
          }
        }

        window.location.reload()
      }
    }

    window.addEventListener('storage', handleStorageChange)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [])

  useEffect(() => {
    checkPathwayChanges()
  }, [checkPathwayChanges])

  const value = useMemo(() => {
    return {
      user,
      checkIsLoggedIn,
      isTrainee: !!user?.type?.some((it) => it === UserType.TRAINEE),
      isIndividualTrainee:
        !!user?.type?.some((it) => it === UserType.TRAINEE) &&
        user?.licenseType === ELicenseType.individual,
      isCareTeam: !!user?.type?.some((it) => it === UserType.CARETEAM),
      isCaregiver: !!user?.type?.some((it) => it === UserType.CAREGIVER),
      isAdmin: !!user?.type?.some((it) => it === UserType.ADMIN),
      isAnalist: !!user?.type?.some((it) => it === UserType.ANALYST),
      isOrgUser: !!user?.type?.some((it) => UserOrganizationalRoles.includes(it)),
      profileName: `${user?.firstname} ${user?.lastname}`,
      position: UserTypeLabels[user?.type?.[0]],
      isSharingEnabled,
      authLoading,
      regLoading,
      login: onLogin,
      register: onRegister,
      clearUserData,
      refreshUser: onAuthenticate,
    }
  }, [
    user,
    checkIsLoggedIn,
    authLoading,
    regLoading,
    onLogin,
    onRegister,
    clearUserData,
    onAuthenticate,
    isSharingEnabled,
  ])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export default AuthProvider
